import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { SERVICE_OPTIONS } from "../content/services";

const EMPTY = {
  nome: "",
  email: "",
  telefono: "",
  citta: "",
  provincia: "",
  servizio: "",
  descrizione: "",
  privacy: false,
  website: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const inputCls = (hasError) =>
  `w-full rounded-xl border bg-white px-4 py-3 text-sm text-antracite outline-none transition-colors placeholder:text-muted/50 focus:border-petrolio focus:ring-2 focus:ring-petrolio/15 ${
    hasError ? "border-red-400" : "border-line"
  }`;

function ErrorText({ id, children }) {
  return (
    <p id={id} className="mt-1.5 text-xs text-red-600">
      {children}
    </p>
  );
}

export default function ContactForm() {
  const [params] = useSearchParams();
  const preselected = params.get("servizio") || "";
  const [values, setValues] = useState({
    ...EMPTY,
    servizio: SERVICE_OPTIONS.includes(preselected) ? preselected : "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const set = (key) => (e) =>
    setValues((v) => ({ ...v, [key]: key === "privacy" ? e.target.checked : e.target.value }));

  const validate = (v) => {
    const e = {};
    if (!v.nome.trim()) e.nome = "Inserisci il tuo nome e cognome.";
    else if (v.nome.trim().length > 120) e.nome = "Massimo 120 caratteri.";
    if (!v.email.trim()) e.email = "Inserisci la tua email.";
    else if (!EMAIL_RE.test(v.email.trim())) e.email = "Inserisci un indirizzo email valido.";
    if (v.telefono.trim() && v.telefono.trim().length > 40) e.telefono = "Massimo 40 caratteri.";
    if (!v.citta.trim()) e.citta = "Inserisci la città dell’intervento o della pratica.";
    else if (v.citta.trim().length > 120) e.citta = "Massimo 120 caratteri.";
    if (!v.provincia.trim()) e.provincia = "Inserisci la provincia.";
    else if (v.provincia.trim().length > 30) e.provincia = "Massimo 30 caratteri.";
    if (!v.servizio) e.servizio = "Seleziona il servizio richiesto.";
    if (!v.descrizione.trim()) e.descrizione = "Descrivi brevemente la tua richiesta.";
    else if (v.descrizione.trim().length > 3000) e.descrizione = "Massimo 3000 caratteri.";
    if (!v.privacy) e.privacy = "È necessario accettare l’informativa privacy.";
    return e;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setStatus("sending");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      let ok = res.ok;
      try {
        const json = await res.json();
        ok = ok && json.ok === true;
      } catch {
        ok = false;
      }
      setStatus(ok ? "success" : "error");
    } catch {
      setStatus("error");
    }
  };

  const reset = () => {
    setValues({ ...EMPTY, servizio: "" });
    setErrors({});
    setStatus("idle");
  };

  if (status === "success") {
    return (
      <div data-testid="form-success" role="status" className="rounded-2xl border border-salvia/40 bg-salvia-tint p-8 text-center">
        <CheckCircle2 size={40} className="mx-auto text-petrolio" />
        <h3 className="mt-4 font-display text-xl font-bold text-antracite">Richiesta inviata correttamente</h3>
        <p className="mt-2 text-sm leading-relaxed text-muted">
          Ti rispondiamo entro 24 ore. Se non trovi la nostra risposta, controlla anche la cartella spam.
        </p>
        <button
          type="button"
          onClick={reset}
          data-testid="form-send-another"
          className="mt-6 text-sm font-semibold text-petrolio underline underline-offset-4 hover:text-petrolio-deep"
        >
          Invia un’altra richiesta
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate data-testid="form-contact" aria-describedby="form-note">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="nome" className="mb-1.5 block text-sm font-medium text-antracite">
            Nome e cognome *
          </label>
          <input
            id="nome"
            name="nome"
            type="text"
            autoComplete="name"
            maxLength={120}
            value={values.nome}
            onChange={set("nome")}
            aria-invalid={!!errors.nome}
            aria-describedby={errors.nome ? "err-nome" : undefined}
            data-testid="form-input-nome"
            className={inputCls(!!errors.nome)}
            placeholder="Il tuo nome"
          />
          {errors.nome && <ErrorText id="err-nome">{errors.nome}</ErrorText>}
        </div>
        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-antracite">
            Email *
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            maxLength={200}
            value={values.email}
            onChange={set("email")}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "err-email" : undefined}
            data-testid="form-input-email"
            className={inputCls(!!errors.email)}
            placeholder="nome@esempio.it"
          />
          {errors.email && <ErrorText id="err-email">{errors.email}</ErrorText>}
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="telefono" className="mb-1.5 block text-sm font-medium text-antracite">
            Telefono
          </label>
          <input
            id="telefono"
            name="telefono"
            type="tel"
            autoComplete="tel"
            maxLength={40}
            value={values.telefono}
            onChange={set("telefono")}
            aria-invalid={!!errors.telefono}
            aria-describedby={errors.telefono ? "err-telefono" : "hint-telefono"}
            data-testid="form-input-telefono"
            className={inputCls(!!errors.telefono)}
            placeholder="+39 …"
          />
          {errors.telefono ? (
            <ErrorText id="err-telefono">{errors.telefono}</ErrorText>
          ) : (
            <p id="hint-telefono" className="mt-1.5 text-[13px] text-muted">
              Facoltativo — se preferisci essere ricontattato telefonicamente
            </p>
          )}
        </div>
        <div>
          <label htmlFor="citta" className="mb-1.5 block text-sm font-medium text-antracite">
            Città dell’intervento o della pratica *
          </label>
          <input
            id="citta"
            name="citta"
            type="text"
            autoComplete="address-level2"
            maxLength={120}
            value={values.citta}
            onChange={set("citta")}
            aria-invalid={!!errors.citta}
            aria-describedby={errors.citta ? "err-citta" : undefined}
            data-testid="form-input-citta"
            className={inputCls(!!errors.citta)}
            placeholder="Es. Milano"
          />
          {errors.citta && <ErrorText id="err-citta">{errors.citta}</ErrorText>}
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="provincia" className="mb-1.5 block text-sm font-medium text-antracite">
            Provincia *
          </label>
          <input
            id="provincia"
            name="provincia"
            type="text"
            autoComplete="address-level1"
            maxLength={30}
            value={values.provincia}
            onChange={set("provincia")}
            aria-invalid={!!errors.provincia}
            aria-describedby={errors.provincia ? "err-provincia" : undefined}
            data-testid="form-input-provincia"
            className={inputCls(!!errors.provincia)}
            placeholder="Sigla o nome"
          />
          {errors.provincia && <ErrorText id="err-provincia">{errors.provincia}</ErrorText>}
        </div>
        <div>
          <label htmlFor="servizio" className="mb-1.5 block text-sm font-medium text-antracite">
            Servizio richiesto *
          </label>
          <select
            id="servizio"
            name="servizio"
            value={values.servizio}
            onChange={set("servizio")}
            aria-invalid={!!errors.servizio}
            aria-describedby={errors.servizio ? "err-servizio" : undefined}
            data-testid="form-select-servizio"
            className={`${inputCls(!!errors.servizio)} appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2216%22 height=%2216%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%235C646B%22 stroke-width=%222%22%3E%3Cpath d=%22m6 9 6 6 6-6%22/%3E%3C/svg%3E')] bg-[position:right_1rem_center] bg-no-repeat pr-10`}
          >
            <option value="" disabled>
              Seleziona un servizio
            </option>
            {SERVICE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {errors.servizio && <ErrorText id="err-servizio">{errors.servizio}</ErrorText>}
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="descrizione" className="mb-1.5 block text-sm font-medium text-antracite">
          Descrizione della richiesta *
        </label>
        <textarea
          id="descrizione"
          name="descrizione"
          rows={5}
          maxLength={3000}
          value={values.descrizione}
          onChange={set("descrizione")}
          aria-invalid={!!errors.descrizione}
          aria-describedby={errors.descrizione ? "err-descrizione" : undefined}
          data-testid="form-input-descrizione"
          className={`${inputCls(!!errors.descrizione)} resize-y`}
          placeholder="Descrivi brevemente la pratica o il servizio di cui hai bisogno"
        />
        {errors.descrizione && <ErrorText id="err-descrizione">{errors.descrizione}</ErrorText>}
      </div>

      {/* Honeypot anti-spam: campo nascosto per le persone, irresistibile per i bot */}
      <div className="hidden" aria-hidden="true">
        <label htmlFor="website">Non compilare questo campo</label>
        <input
          id="website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={set("website")}
          data-testid="form-honeypot"
        />
      </div>

      <div className="mt-5">
        <label htmlFor="privacy" className="flex items-start gap-3 text-sm text-muted">
          <input
            id="privacy"
            name="privacy"
            type="checkbox"
            checked={values.privacy}
            onChange={set("privacy")}
            aria-invalid={!!errors.privacy}
            aria-describedby={errors.privacy ? "err-privacy" : undefined}
            data-testid="form-check-privacy"
            className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-line"
          />
          <span>
            Ho letto la{" "}
            <Link to="/privacy-policy" className="font-medium text-petrolio underline underline-offset-2">
              Privacy Policy
            </Link>{" "}
            e acconsento al trattamento dei miei dati per ricevere risposta alla richiesta. *
          </span>
        </label>
        {errors.privacy && <ErrorText id="err-privacy">{errors.privacy}</ErrorText>}
      </div>

      {status === "error" && (
        <div
          data-testid="form-error"
          role="alert"
          className="mt-5 flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          <AlertCircle size={18} className="mt-0.5 shrink-0" />
          <span>Invio non riuscito. Controlla la connessione e riprova; se il problema persiste, riprova tra qualche minuto.</span>
        </div>
      )}

      <div className="mt-6">
        <button
          type="submit"
          disabled={status === "sending"}
          data-testid="form-submit-button"
          className="btn-primary w-full sm:w-auto"
        >
          {status === "sending" ? "Invio in corso…" : "Invia la richiesta"}
        </button>
        <p id="form-note" className="mt-4 text-[13px] leading-relaxed text-muted">
          Dopo aver ricevuto la richiesta potremo contattarti per approfondire il caso e richiedere eventuale documentazione.
        </p>
        <p className="mt-2 text-[13px] leading-relaxed text-muted">
          L’invio del modulo è una richiesta di contatto: non costituisce conferimento di incarico e non genera obblighi economici.
        </p>
      </div>
    </form>
  );
}
